/** Copy for `details.code` sub-codes (FRONTEND_PLAN §4.9). Split from error-copy.ts to keep
 *  both files inside the 300-line budget; `describeError` reads the sub-code map first. */
import type { Copy } from './error-copy'

export const BY_SUB_CODE: Readonly<Record<string, Copy>> = {
  BILLING_NOT_LINKED: {
    title: 'Billing is not linked',
    message: 'This workspace has no billing account yet.',
    retryable: false,
  },
  NO_CONNECT_ACCOUNT: {
    title: 'No payout account',
    message: 'No Connect account exists for this reseller yet.',
    retryable: false,
  },
  CANNOT_DISCONNECT_WHILE_CALENDAR_SOURCE_EXTERNAL: {
    title: 'Calendar still in use',
    message: 'Switch the organisation’s calendar mode back to internal before disconnecting.',
    retryable: false,
  },
  INVALID_RULE_CONFIG: {
    title: 'The rule is not valid',
    message: 'Fix the values listed below and try again.',
    retryable: false,
  },
  INVALID_TEMPLATE_BODY: {
    title: 'This template cannot be rendered',
    message: 'Fix the placeholders listed below and try again.',
    retryable: false,
  },
  TEMPLATE_VARIABLES_MISSING: {
    title: 'Sample values missing',
    message: 'Give every variable in the body a value to preview it.',
    retryable: false,
  },
  TEMPLATE_NOT_FOUND: {
    title: 'Template not found',
    message: 'That template version no longer exists.',
    retryable: false,
  },
  REPLY_NOT_DELIVERABLE: {
    title: 'This customer cannot be messaged',
    message:
      'There is no way to reach them. They may have asked not to be contacted, their details may have been removed, or they haven’t agreed to texts or email.',
    retryable: false,
  },
  NO_CONSENT_FOR_CHANNEL: {
    title: 'No consent for that channel',
    message: 'The customer has not agreed to be contacted this way.',
    retryable: false,
  },
  RECOVERY_CASE_ALREADY_OPEN: {
    title: 'Case already open',
    message: 'This appointment already has an open recovery case.',
    retryable: false,
  },
  APPOINTMENT_NOT_RECOVERABLE: {
    title: 'Not recoverable',
    message: 'A recovery case can only be opened for a cancelled, missed or at-risk appointment.',
    retryable: false,
  },
  CANDIDATE_NOT_OFFERABLE: {
    title: 'Can’t send this offer',
    message: 'This customer has already answered, or no longer qualifies for this opening.',
    retryable: false,
  },
  CANDIDATE_ALREADY_ANSWERED: {
    title: 'Already answered',
    message: 'This customer has already replied to the offer.',
    retryable: false,
  },
  SLOT_REWRITE_NOT_SUPPORTED: {
    title: 'Time cannot be changed here',
    message: 'Manual booking fills the slot as it stands. Reschedule the appointment to move it.',
    retryable: false,
  },
  ILLEGAL_TRACK_TRANSITION: {
    title: 'Can’t make that change',
    message: 'This recovery case can’t move to that stage from where it is now.',
    retryable: false,
  },
  HOLD_ALREADY_CONSUMED: {
    title: 'Hold already used',
    message: 'That hold has already been turned into a booking.',
    retryable: false,
  },
  SLOT_ALREADY_HELD: {
    title: 'Slot already held',
    message: 'Someone else is holding this slot right now.',
    retryable: false,
  },
  SLOT_NO_LONGER_AVAILABLE: {
    title: 'Slot taken',
    message: 'That appointment is no longer available.',
    retryable: false,
  },
  SLOT_NOT_OPEN: {
    title: 'Slot not open',
    message: 'This appointment is not open for booking.',
    retryable: false,
  },
  SLOT_UNAVAILABLE: {
    title: 'Time unavailable',
    message: 'That time is no longer free. Pick another slot.',
    retryable: false,
  },
  HOLD_EXPIRED: {
    title: 'Hold expired',
    message: 'The reservation on this slot ran out.',
    retryable: false,
  },
  ILLEGAL_STATE_TRANSITION: {
    title: 'Not allowed right now',
    message: 'That change isn’t allowed for an appointment with its current status.',
    retryable: false,
  },
  TERMINAL_TRANSITION_REQUIRES_STAFF: {
    title: 'Staff action required',
    message: 'Only a staff member can reverse a completed or cancelled appointment.',
    retryable: false,
  },
  CUSTOMER_DO_NOT_BOOK: {
    title: 'Customer cannot be booked',
    message: 'This customer is marked do-not-book.',
    retryable: false,
  },
  CUSTOMER_NOT_OPTED_IN: {
    title: 'Not opted in',
    message: 'This customer has not opted in to the waitlist.',
    retryable: false,
  },
  CASE_ALREADY_CLOSED: {
    title: 'Case closed',
    message: 'This recovery case is already closed.',
    retryable: false,
  },
  PAYMENTS_UNAVAILABLE: {
    title: 'Billing not connected',
    message: 'Billing isn’t connected for this workspace yet.',
    retryable: false,
  },
  DATABASE_AT_CAPACITY: {
    title: 'Busy right now',
    message: 'The service is at capacity. Please retry shortly.',
    retryable: true,
  },
  RANGE_TOO_LARGE: {
    title: 'Range too large',
    message: 'Narrow the date range and try again.',
    retryable: false,
  },
  EXPORT_TOO_LARGE: {
    title: 'Export too large',
    message: 'Narrow the date range to export.',
    retryable: false,
  },
  MISSING_EXPORT_PERMISSION: {
    title: 'Not permitted',
    message: 'You don’t have permission to export financial reports.',
    retryable: false,
  },
  EMAIL_ALREADY_EXISTS: {
    title: 'Email in use',
    message: 'A user with that email address already exists.',
    retryable: false,
  },
  CANNOT_GRANT_HIGHER_ROLE: {
    title: 'Not permitted',
    message: 'You can’t grant a role above your own.',
    retryable: false,
  },
  DUPLICATE_WAITLIST_ENTRY: {
    title: 'Already on the waitlist',
    message: 'This customer already has an active entry for that service and location.',
    retryable: false,
  },
  RESOURCES_NOT_AVAILABLE_IN_LAYER_MODE: {
    title: 'Not available in Layer Mode',
    message: 'Resources are managed by the external calendar while Layer Mode is active.',
    retryable: false,
  },
  SCHEDULE_OVERLAP: {
    title: 'Blocks overlap',
    message: 'Two schedule blocks overlap on the same day.',
    retryable: false,
  },
  TIME_OFF_OVERLAP: {
    title: 'Time off overlaps',
    message: 'That period overlaps existing time off for this provider.',
    retryable: false,
  },
  INVALID_LOCATION_IDS: {
    title: 'Invalid locations',
    message: 'One or more locations don’t belong to this organization.',
    retryable: false,
  },
}
